// aluno/lgpd-footer.js — Rodapé permanente com links LGPD.
//
// Injetado no <body> no boot do app (main.js), fora do #app — sobrevive
// a cada re-render de rota. Aparece em todas as rotas autenticadas e some
// na tela de login e rotas de leitura full-screen (/termo, /privacidade).
//
// Links:
//   - Termo de Consentimento  → #/termo         (H3)
//   - Política de Privacidade → #/privacidade   (H3)
//   - Canal do Titular        → mailto + copy para clipboard
//   - Gerenciar meus consentimentos → #/consentimentos (H5, placeholder)
//
// Contrato público:
//   initLgpdFooter() — chama uma vez no boot; idempotente.
//
// Decisões travadas:
//   - Um único nó DOM fixo no body, classe .lgpd-footer
//   - Visibilidade controlada por classe .lgpd-footer-hidden no body
//   - Rotas ocultas: '/login', '/termo', '/privacidade'
//   - Sem dependência de framework de rota — escuta hashchange direto
//   - Mailto como fallback + botão "Copiar email" para UX

import { getConsentTermoVersion } from './consent-modal.js';

const CANAL_TITULAR_EMAIL = 'contato@eloquence.com.br';
const ROTAS_OCULTAS = new Set(['/login', '/termo', '/privacidade', '']);

let _inited = false;
let _footerEl = null;

// ── API pública ──────────────────────────────────────────────────────────────

export function initLgpdFooter() {
  if (_inited) return;
  _inited = true;

  _footerEl = document.createElement('footer');
  _footerEl.className = 'lgpd-footer';
  _footerEl.setAttribute('role', 'contentinfo');
  _footerEl.setAttribute('aria-label', 'Informações de privacidade');
  _footerEl.innerHTML = _renderHtml();

  document.body.appendChild(_footerEl);

  _bindEvents();
  _syncVisibility();

  window.addEventListener('hashchange', _syncVisibility);
}

// ── Render ───────────────────────────────────────────────────────────────────

function _renderHtml() {
  const version = getConsentTermoVersion();
  return `
    <div class="lgpd-footer-inner">
      <nav class="lgpd-footer-links" aria-label="Links LGPD">
        <a href="#/termo" class="lgpd-footer-link lgpd-footer-link-muted">Termo de Consentimento</a>
        <span class="lgpd-footer-sep" aria-hidden="true">·</span>
        <a href="#/privacidade" class="lgpd-footer-link lgpd-footer-link-muted">Política de Privacidade</a>
        <span class="lgpd-footer-sep" aria-hidden="true">·</span>
        <button type="button" class="lgpd-footer-link lgpd-footer-link-muted lgpd-footer-btn" data-lgpd-action="canal">
          Canal do Titular
        </button>
        <span class="lgpd-footer-sep" aria-hidden="true">·</span>
        <a href="#/consentimentos" class="lgpd-footer-link lgpd-footer-link-muted">Gerenciar meus consentimentos</a>
      </nav>
      <div class="lgpd-footer-meta">
        <span class="lgpd-footer-controlador">
          ELOQUENCE TREINAMENTO LTDA · CNPJ 19.716.643/0001-64
        </span>
        <span class="lgpd-footer-versao" title="Versão do Termo aceita">
          Termo v${version}
        </span>
      </div>
    </div>
  `;
}

// ── Eventos ──────────────────────────────────────────────────────────────────

function _bindEvents() {
  _footerEl?.querySelector('[data-lgpd-action="canal"]')
    ?.addEventListener('click', _onCanalClick);
}

async function _onCanalClick() {
  // Abre cliente de email + copia pro clipboard como fallback
  const subject = encodeURIComponent('Solicitação LGPD — titular de dados');
  const body = encodeURIComponent(
    'Olá, sou aluno(a)/ex-aluno(a) da Eloquence e gostaria de exercer meu direito previsto no Art. 18 da LGPD.\n\n' +
    'Nome completo:\nCPF:\nDireito solicitado:\nDetalhes:\n'
  );
  const mailto = `mailto:${CANAL_TITULAR_EMAIL}?subject=${subject}&body=${body}`;

  // Tenta abrir cliente de email
  try {
    window.location.href = mailto;
  } catch {
    // fallback silencioso
  }

  // Copia email para clipboard para facilitar
  try {
    if (navigator.clipboard?.writeText) {
      await navigator.clipboard.writeText(CANAL_TITULAR_EMAIL);
      _flashTooltip('Email copiado: ' + CANAL_TITULAR_EMAIL);
    }
  } catch {
    // sem clipboard? tudo bem, o mailto já foi disparado
  }
}

function _flashTooltip(texto) {
  const t = document.createElement('div');
  t.className = 'lgpd-footer-toast';
  t.textContent = texto;
  document.body.appendChild(t);
  // Remove após a animação CSS
  setTimeout(() => { t.parentNode?.removeChild(t); }, 2400);
}

// ── Visibilidade ─────────────────────────────────────────────────────────────

function _syncVisibility() {
  if (!_footerEl) return;
  const hash = window.location.hash.replace(/^#/, '');
  // hash pode ser '/aluno' ou '/aluno/frequencia' etc.
  // Consideramos apenas o primeiro segmento para decidir visibilidade.
  const rotaBase = '/' + (hash.split('/')[1] || '');
  const raiz = hash === '' || hash === '/' ? '' : rotaBase;

  if (ROTAS_OCULTAS.has(raiz) || ROTAS_OCULTAS.has(hash)) {
    _footerEl.classList.add('lgpd-footer-hidden');
  } else {
    _footerEl.classList.remove('lgpd-footer-hidden');
  }
}

// ── Destructor (para testes) ────────────────────────────────────────────────

export function destroyLgpdFooter() {
  if (_footerEl?.parentNode) _footerEl.parentNode.removeChild(_footerEl);
  window.removeEventListener('hashchange', _syncVisibility);
  _footerEl = null;
  _inited = false;
}
